import { useTranslation } from "react-i18next";
import { PageSEO } from "@/components/seo/PageSEO";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, Wrench, ShoppingCart, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LogoTicker } from "@/components/shared/LogoTicker";
import { FeaturedCase } from "@/components/shared/FeaturedCase";
import { Testimonials } from "@/components/shared/Testimonials";
import { useLanguage } from "@/hooks/useLanguage";

const segments = [
  {
    key: "transport",
    icon: Truck,
    href: "/losningar/transport",
    highlights: ["Postnummerbaserade zoner", "Leveransplanering", "Adresskvalitet"],
  },
  {
    key: "service",
    icon: Wrench,
    href: "/losningar/service",
    highlights: ["Serviceområden", "Resursfördelning", "Tidsplanering"],
  },
  {
    key: "ecommerce",
    icon: ShoppingCart,
    href: "/losningar/ehandel",
    highlights: ["Kassavalidering", "Leveranszoner", "Nordisk expansion"],
  },
];

const Losningar = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  return (
    <div>
      <PageSEO title={t("nav.losningar")} description={t("seo.losningar")} />
      {/* Hero */}
      <section className="relative bg-primary text-primary-foreground py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80" />
        <div className="absolute top-10 right-10 w-64 h-64 rounded-full border border-primary-foreground/5" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full border border-primary-foreground/5 translate-y-1/2" />
        <div className="absolute top-1/2 right-1/3 w-40 h-40 rounded-full bg-coral/5" />

        <div className="relative container mx-auto px-4 lg:px-8 text-center max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-block text-sm font-bold font-subheading uppercase tracking-widest text-coral mb-4">
              {t("solutionPages.overview.tagline")}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium font-heading leading-tight tracking-tight">
              {t("solutionPages.overview.title")}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-primary-foreground/80 font-body leading-relaxed">
              {t("solutionPages.overview.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Segment Cards */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {segments.map((segment, index) => (
              <motion.div
                key={segment.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={getLocalizedPath(segment.href)} className="group block h-full">
                  <div className="rounded-2xl border border-border overflow-hidden hover:shadow-lg transition-all duration-300 h-full flex flex-col">
                    <div className="bg-petrol-light/50 p-6 flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <segment.icon className="h-7 w-7 text-primary dark:text-white" />
                      </div>
                      <h2 className="text-2xl font-bold font-subheading text-foreground">
                        {t(`solutionPages.overview.segments.${segment.key}.title`)}
                      </h2>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <p className="text-muted-foreground font-body leading-relaxed mb-4">
                        {t(`solutionPages.overview.segments.${segment.key}.description`)}
                      </p>
                      <div className="mt-auto">
                        <div className="flex flex-wrap gap-2 mb-6">
                          {segment.highlights.map((highlight) => (
                            <span
                              key={highlight}
                              className="text-xs bg-petrol-light rounded-full px-3 py-1 text-primary font-medium"
                            >
                              {highlight}
                            </span>
                          ))}
                        </div>
                        <span className="inline-flex items-center text-sm font-medium text-coral group-hover:gap-2 transition-all">
                          {t("solutionPages.readMore")} {t(`solutionPages.overview.segments.${segment.key}.title`).toLowerCase()} <ArrowRight className="ml-1 h-4 w-4" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <LogoTicker
        title={t("customerCases.logos.title")}
        subtitle={t("customerCases.logos.subtitle")}
      />

      <FeaturedCase />
      <Testimonials />

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-medium font-heading tracking-tight">
              {t("solutionPages.overview.cta.title")}
            </h2>
            <p className="mt-4 text-lg text-primary-foreground/70 font-body">
              {t("solutionPages.overview.cta.subtitle")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                asChild
                className="bg-coral text-coral-foreground hover:bg-coral/90 font-semibold shadow-lg shadow-coral/25 text-base px-8"
              >
                <Link to={getLocalizedPath("/kontakt?type=demo")}>
                  {t("solutionPages.ctaButton")}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                asChild
                className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 font-medium text-base"
              >
                <Link to={getLocalizedPath("/tjanster")}>{t("solutionPages.ctaSecondary")}</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Losningar;
